// Need to use the React-specific entry point to import createApi
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// Define a service using a base URL and expected endpoints
export const disputeService = createApi({
  reducerPath: 'disputeService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['DISPUTE_DETAILS'],
  endpoints: ({ query, mutation }) => ({
    getDelevrablesist: query({
      query: ({ jobId }) => {
        return {
          url: `/jobs/v1/operator/${jobId}/deliverables`,
          headers: { ActiveProfile: '1' },
        };
      },
    }),

    postFriendlyStage: mutation({
      query: ({ body, disputeId }) => ({
        url: `/jobs/v1/job-disputes-operator/${disputeId}/friendly-stage`,
        method: 'POST',
        body,
      }),
    }),
    createChat: mutation({
      query: ({ body, disputeId }) => ({
        url: `/jobs/v1/job-disputes-operator/${disputeId}/create-chat`,
        method: 'POST',
        body,
      }),
    }),
    scheduleInterview: mutation({
      query: ({ body, disputeId }) => ({
        url: `/jobs/v1/job-disputes-operator/${disputeId}/create-interview`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DISPUTE_DETAILS'],
    }),
    LeaveMeeting: mutation({
      query: ({ disputeId }) => ({
        url: `/jobs/v1/job-disputes-operator/${disputeId}/leave-operation-interview`,
        method: 'PUT',
      }),
    }),
    startAction: mutation({
      query: ({ disputeId }) => ({
        url: `/jobs/v1/job-disputes-operator/${disputeId}/start-action`,
        method: 'PUT',
      }),
    }),
    goInterviewMeeting: query({
      query: ({ disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}/go-operation-interview`,
        };
      },
    }),
    getTalentInfo: query({
      query: ({ disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}/talent-info `,
        };
      },
    }),
    getDisputeDetails: query({
      query: ({ disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}`,
        };
      },
      providesTags: ['DISPUTE_DETAILS'],
    }),
    getDisputeDecisions: query({
      query: ({ disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}/decisions`,
        };
      },
      providesTags: ['DISPUTE_DETAILS'],
    }),
    getAgainstLookups: query({
      query: ({ disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}/against`,
        };
      },
    }),
    takeDecision: mutation({
      query: ({ body, disputeId }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/${disputeId}/take-decision`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: ['DISPUTE_DETAILS'],
    }),

    upLoadDocs: mutation({
      query: ({ files }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/upload-docs`,
          headers: {
            'ACCEPT-CONTENT-TYPE': 'multipart/form-data', // <-- required by backend
          },
          method: 'POST',
          body: files,
        };
      },
    }),

    getDisputeHistory: query({
      query: ({ jobId, searchTerm }) => {
        return {
          url: `/jobs/v1/job-disputes-operator/jobs/${jobId}/history`,
          params: {
            search: searchTerm,
          },
        };
      },
    }),
    getJobInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/info`,
          headers: { ActiveProfile: '1' },
        };
      },
    }),

    getSkillsInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/category-speciality-skills`,
        };
      },
    }),
    getScopeOfWorkInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/preview-estimate`,
        };
      },
    }),
    getAddtionalInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/additional-info`,
        };
      },
    }),
    getBudgetInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/budget`,
        };
      },
    }),
    getClientInfoDispute: query({
      query: ({ jobId }) => {
        return {
          url: `/jobs/v1/operator/${jobId}/postActivities`,
        };
      },
    }),
    getTalentInfoDispute: query({
      query: ({ profileId }) => {
        return {
          url: `/v1/users/${profileId}/job_about_info`,
        };
      },
    }),
    getQuestionInfoDispute: query({
      query: ({ jobId, jobType }) => {
        return {
          url: `/jobs/v1/operator/${jobType}/${jobId}/questions`,
        };
      },
    }),
    getTimerInfoDispute: query({
      query: ({ jobId }) => {
        return {
          url: `/jobs/v1/operator/${jobId}/counter`,
        };
      },
    }),
    getMessageHistory: query({
      query: ({ proposalId, page = 0, limit = 20 }) => {
        return {
          url: `/chat-service/v1/operator/chat/history/${proposalId}`,
          params: { page, limit },
        };
      },
    }),

    getContractInfoDispute: query({
      query: ({ contractId }) => {
        return {
          url: `/jobs/v1/operator/contract/detail/${contractId}`,
        };
      },
    }),

    getJobRequestsDispute: query({
      query: ({ jobId }) => {
        return {
          url: `/jobs/v1/operator/${jobId}/job-requests`,
        };
      },
    }),
    // ------------------------------------------------------------------------------
    getOrderPaymentSummary: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/payment-summary`,
        };
      },
    }),
    getRequestSummary: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/summary`,
        };
      },
    }),
    getDisputedProducts: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/dispute/products/disputes/${params?.disputeId}`,
        };
      },
    }),
    getDisputeSummary: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/dispute-summary`,
        };
      },
    }),
    getBuyerInfoDispute: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/buyer-details`,
        };
      },
    }),
    getSellerInfoDispute: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/seller-details`,
        };
      },
    }),
    getDisputeStats: query({
      query: (params) => {
        return {
          url: `/dispute-service/v1/disputes/${params?.disputeId}/stats`,
        };
      },
    }),

    getDisputeList: query({
      query: (params) => {
        console.log('params', params);
        return {
          url: `/dispute-service/v1/disputes/admin-disputes`,
          params: {
            search: params?.search ? params?.search : undefined,
            dateFrom: params?.fromDate || undefined,
            dateTo: params?.toDate || undefined,
            userType: params?.customerType || undefined,
            statuses: !!params?.status?.length ? params?.status : undefined,
            page: params?.page || '0',
            limit: params?.limit,
          },
        };
      },
    }),

    getDisputeStatuesLK: query({
      query: () => {
        return {
          url: `/dispute-service/v1/dispute-statuses/admin-dispute-statuses`,
        };
      },
    }),

    submitDispute: mutation({
      query: ({ disputeId, body }) => ({
        url: `/dispute-service/v1/disputes/${disputeId}/submit-admin-actions`,
        method: 'POST',
        body,
      }),
    }),

    moveToArbiration: mutation({
      query: ({ disputeId }) => ({
        url: `/dispute-service/v1/disputes/${disputeId}/move-to-arbitration`,
        method: 'POST',
      }),
    }),

    getDisputeActions: query({
      query: ({ disputeId }) => {
        return {
          url: `/dispute-service/v1/disputes/${disputeId}/admin-actions`,
        };
      },
    }),

    canTakeAction: query({
      query: ({ disputeId }) => {
        return {
          url: `/dispute-service/v1/disputes/${disputeId}/validate-admin-actions`,
        };
      },
    }),

    getDisputeActionsPreview: query({
      query: ({ disputeId }) => {
        return {
          url: `/dispute-service/v1/disputes/${disputeId}/arbitration-negotiation-summary`,
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDisputeListQuery,
  useGetAgainstLookupsQuery,
  useGetDisputeDecisionsQuery,
  useTakeDecisionMutation,
  useUpLoadDocsMutation,
  useGetDelevrablesistQuery,
  usePostFriendlyStageMutation,
  useGetDisputeDetailsQuery,
  useGetDisputeHistoryQuery,
  useGetJobInfoDisputeQuery,
  useGetJobRequestsDisputeQuery,
  useCreateChatMutation,
  useScheduleInterviewMutation,
  useLazyGoInterviewMeetingQuery,
  useGetSkillsInfoDisputeQuery,
  useGetScopeOfWorkInfoDisputeQuery,
  useGetAddtionalInfoDisputeQuery,
  useGetBudgetInfoDisputeQuery,
  useGetClientInfoDisputeQuery,
  useGetQuestionInfoDisputeQuery,
  useGetTimerInfoDisputeQuery,
  useGetContractInfoDisputeQuery,
  useGetMessageHistoryQuery,
  useLeaveMeetingMutation,
  useGetTalentInfoDisputeQuery,
  useGetTalentInfoQuery,
  useStartActionMutation,
  useGetOrderPaymentSummaryQuery,
  useGetRequestSummaryQuery,
  useGetDisputedProductsQuery,
  useGetDisputeSummaryQuery,
  useGetBuyerInfoDisputeQuery,
  useGetSellerInfoDisputeQuery,
  useGetDisputeStatsQuery,
  useGetDisputeStatuesLKQuery,
  useSubmitDisputeMutation,
  useGetDisputeActionsQuery,
  useCanTakeActionQuery,
  useMoveToArbirationMutation,
  useGetDisputeActionsPreviewQuery,
} = disputeService;
